import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { AnalysisFeeCalculatorDTO } from "@models/platform-analysis/entities/steps/fee";
import { FeeCalculatorSectionId } from "@models/platform-analysis/enums/fee/feeCalculatorSectionId";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { RootState } from "@redux/store";
import { memo, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { useUnmount } from "react-use";

export const FeeCalculatorFormValidStatesTracker = memo(() => {
  const {
    formState: { isDirty },
  } = useFormContext<AnalysisFeeCalculatorDTO>();
  const { setIsDirty, setIsValid, setIsValidPortfolioSetups } =
    usePlatformAnalysis();

  const dirtyRef = useRef<boolean>(false);
  const errorRef = useRef<boolean>(false);
  const portfolioSetupErrorRef = useRef<boolean>();

  const errorStates = useSelector(
    (state: RootState) =>
      state.platformAnalysis.internal.feeCalculator.sectionsHaveError,
  );
  const portfolioLevel = useSelector(
    (state: RootState) => state.platformAnalysis?.ui?.portfolioLevel,
  );

  const hasErrors = useMemo(
    () =>
      Object.keys(errorStates).some(
        (key) =>
          key !== FeeCalculatorSectionId.PortfolioSetup &&
          errorStates[key as FeeCalculatorSectionId],
      ),
    [errorStates],
  );

  const portfolioSetupHasErrors = useMemo(
    () =>
      !!portfolioLevel &&
      portfolioLevel !== FeeCalculatorPortfolioLevel.No &&
      Boolean(errorStates[FeeCalculatorSectionId.PortfolioSetup]),
    [errorStates, portfolioLevel],
  );

  if (dirtyRef.current !== isDirty) {
    dirtyRef.current = isDirty;
    setIsDirty(isDirty);
  }

  if (errorRef.current !== hasErrors) {
    errorRef.current = hasErrors;
    setIsValid(!hasErrors);
  }

  if (portfolioSetupErrorRef.current !== portfolioSetupHasErrors) {
    portfolioSetupErrorRef.current = portfolioSetupHasErrors;
    setIsValidPortfolioSetups(!portfolioSetupErrorRef.current);
  }

  useUnmount(() => {
    setIsDirty(false);
    setIsValid(true);
  });

  return <></>;
});
